/**
 * web/src/lib/sponsorApprovalKinds.ts
 * Review-queue kinds for SPONSOR email gates, so the sponsor approvals page
 * can step through many welcome or Exhibit Next Steps emails in one pass (FP
 * Connect 2026-10: 41 sponsors started from the Tracker at once; FPC27 the
 * same for next steps). Same `ApprovalKind` shape the speaker registry uses;
 * the queue reads only verb/label/what/stepIndex.
 */
import type { PendingGate } from '../api/gates';
import type { ApprovalKind } from './approvalKinds';
import type { ApprovalRow } from './approvalsDerive';
import { getStepCount, getStepDefs } from './workflow-step-defs';

const SPONSOR_STEP_IDS = getStepDefs('sponsor_onboarding').map((s) => s.id);
const SPONSOR_STEP_COUNT = getStepCount('sponsor_onboarding');

/** The sponsor welcome email gate as a review-queue kind. */
export const SPONSOR_WELCOME_KIND: ApprovalKind = {
  id: 'welcome_email',
  label: 'Welcome email',
  verb: 'Send welcome email',
  what: "Sends the welcome email from your Gmail, marks the Tracker's Welcome Email Sent, and starts watching for the sponsor's reply.",
  notBlocking: 'Nothing sends until you approve — the sponsor simply stays un-welcomed.',
  payload: 'email',
  stepIndex: Math.max(0, SPONSOR_STEP_IDS.indexOf('welcome_email')),
  band: 0,
  stepCount: SPONSOR_STEP_COUNT,
};

/** True for a pending sponsor welcome-email gate. */
export function isSponsorWelcomeGate(g: PendingGate): boolean {
  return (
    g.workflow.type === 'sponsor_onboarding' &&
    g.stepId === 'welcome_email' &&
    g.status === 'waiting_approval'
  );
}

/** The sponsor Exhibit Next Steps (ops) email gate as a review-queue kind. */
export const SPONSOR_OPS_KIND: ApprovalKind = {
  id: 'ops_next_steps',
  label: 'Exhibit next steps email',
  verb: 'Send next steps email',
  what: "Replies on the welcome thread from your Gmail with the exhibit next steps, marks the Tracker's Exhibit Next Steps Email Sent, and starts watching for the sponsor's reply.",
  notBlocking: 'Nothing sends until you approve. The sponsor simply waits for their next steps.',
  payload: 'email',
  stepIndex: Math.max(0, SPONSOR_STEP_IDS.indexOf('ops_next_steps')),
  band: 0,
  stepCount: SPONSOR_STEP_COUNT,
};

/** True for a pending sponsor Exhibit Next Steps gate. */
export function isSponsorOpsGate(g: PendingGate): boolean {
  return (
    g.workflow.type === 'sponsor_onboarding' &&
    g.stepId === 'ops_next_steps' &&
    g.status === 'waiting_approval'
  );
}

function sponsorRows(gates: PendingGate[], kind: ApprovalKind): ApprovalRow[] {
  return gates.map((gate) => ({
    gate,
    kind,
    speaker: gate.workflow.displayName.replace(/\s+—\s+Sponsor Onboarding$/, ''),
    company: null,
    jobTitle: null,
    returning: false,
  }));
}

/**
 * Queue items for the given welcome gates. `speaker` carries the sponsor's
 * company (the workflow display name minus the " — Sponsor Onboarding" tail).
 */
export function sponsorWelcomeRows(gates: PendingGate[]): ApprovalRow[] {
  return sponsorRows(gates.filter(isSponsorWelcomeGate), SPONSOR_WELCOME_KIND);
}

/** Queue items for the given Exhibit Next Steps gates (same naming as welcome). */
export function sponsorOpsRows(gates: PendingGate[]): ApprovalRow[] {
  return sponsorRows(gates.filter(isSponsorOpsGate), SPONSOR_OPS_KIND);
}
